import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, Clock, Trash2, Copy, Star, Check, CheckSquare, Square, Palette, GitCompare, X } from 'lucide-react';
import { useColorHistory } from '../hooks/useColorHistory';
import { useColorActions } from '../hooks/useColorActions';
import { usePaletteContext } from '../contexts/PaletteContext';
import type { ColorHistoryRecord } from '../lib/database';
import { useAlert } from '@ascend/alert';

interface ColorHistoryPanelProps {
  onSelectColor: (hex: string) => void;
  currentHex?: string;
}

export function ColorHistoryPanel({ onSelectColor, currentHex }: ColorHistoryPanelProps) {
  const { history, loading, error, clearHistory, refresh } = useColorHistory(50);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedHexes, setSelectedHexes] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  const { refreshPalettes } = usePaletteContext();

  // Re-fetch history whenever the active color changes
  useEffect(() => {
    if (currentHex) {
      void refresh();
    }
  }, [currentHex, refresh]);

  const { confirm } = useAlert();

  const handleClear = async () => {
    const ok = await confirm({ title: 'Clear history?', message: 'Clear all color history? This cannot be undone.', danger: true });
    if (ok) {
      try {
        await clearHistory();
      } catch (err) {
        console.error('Failed to clear history:', err);
      }
    }
  };

  const toggleSelect = useCallback((hex: string) => {
    setSelectedHexes(prev => {
      const next = new Set(prev);
      if (next.has(hex)) next.delete(hex);
      else next.add(hex);
      return next;
    });
  }, []);

  const exitSelecting = useCallback(() => {
    setIsSelecting(false);
    setSelectedHexes(new Set());
  }, []);

  const handleCreatePalette = useCallback(async () => {
    if (selectedHexes.size === 0) return;
    const colors = Array.from(selectedHexes);
    // Navigate to palette with colors in route state
    navigate('/palette', { state: { colors, from: 'history' } });
    exitSelecting();
  }, [selectedHexes, navigate, exitSelecting]);

  const handleCompare = useCallback(() => {
    const colors = Array.from(selectedHexes);
    if (colors.length !== 2) return;
    navigate(`/compare?fg=${colors[0].replace('#', '')}&bg=${colors[1].replace('#', '')}`);
    exitSelecting();
  }, [selectedHexes, navigate, exitSelecting]);

  if (loading) {
    return (
      <div className="hist-section">
        <div className="flex items-center justify-center py-8">
          <Clock className="w-5 h-5 animate-spin text-gray-400" />
          <span className="ml-2 text-sm text-gray-500">Loading history...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="hist-section" style={{ borderColor: 'var(--bad)' }}>
        <div className="text-sm" style={{ color: 'var(--bad)' }}>{error}</div>
      </div>
    );
  }

  return (
    <div className="hist-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5" style={{ color: 'var(--ink-2)' }} />
          <h3 className="hist-header__title">Recent Colors</h3>
          <span className="hist-header__count">({history.length})</span>
        </div>
        <div className="flex items-center gap-2">
          {history.length > 0 && !isSelecting && (
            <button
              onClick={() => setIsSelecting(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                padding: '3px 10px', fontSize: '12px', fontWeight: 500,
                border: '1px solid var(--line, #e5e7eb)',
                borderRadius: 'var(--radius, 6px)',
                background: 'var(--panel, #fff)',
                color: 'var(--ink-2, #6b7280)',
                cursor: 'pointer',
              }}
              title="Select multiple colors"
            >
              <CheckSquare size={13} />
              Select
            </button>
          )}
          {isSelecting && (
            <button
              onClick={exitSelecting}
              style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                padding: '3px 10px', fontSize: '12px', fontWeight: 500,
                border: '1px solid var(--line, #e5e7eb)',
                borderRadius: 'var(--radius, 6px)',
                background: 'var(--panel, #fff)',
                color: 'var(--ink-2, #6b7280)',
                cursor: 'pointer',
              }}
            >
              <X size={13} />
              Cancel
            </button>
          )}
          {history.length > 0 && !isSelecting && (
            <button
              onClick={handleClear}
              className="hist-header__clear-btn"
              title="Clear history"
            >
              <Trash2 className="w-3 h-3" />
              Clear
            </button>
          )}
        </div>
      </div>

      {history.length === 0 ? (
        <div className="hist-empty">
          <Clock className="hist-empty__icon" />
          <p className="text-sm">No color history yet</p>
          <p className="text-xs mt-1">Colors you convert will appear here</p>
          <button
            type="button"
            onClick={() => onSelectColor('dodgerblue')}
            className="hist-empty__cta mt-3"
          >
            Try "dodgerblue"
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-8 sm:grid-cols-10 md:grid-cols-12 lg:grid-cols-16 gap-2">
          {history.map((item) => (
            <ColorHistoryItem
              key={item.id}
              item={item}
              isActive={currentHex?.toLowerCase() === item.hex.toLowerCase()}
              isSelecting={isSelecting}
              isSelected={selectedHexes.has(item.hex)}
              onUse={() => onSelectColor(item.hex)}
              onToggleSelect={() => toggleSelect(item.hex)}
            />
          ))}
        </div>
      )}

      {/* ── Floating action bar ──────────────────────────────────────── */}
      {isSelecting && selectedHexes.size > 0 && (
        <div style={{
          position: 'sticky', bottom: '16px',
          marginTop: '16px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          padding: '10px 16px',
          background: 'var(--ink, #111)', color: '#fff',
          borderRadius: 'var(--radius-xl, 12px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
          fontSize: '13px', fontWeight: 500,
        }}>
          <span style={{ opacity: 0.7 }}>{selectedHexes.size} selected</span>
          <div style={{ width: '1px', height: '16px', background: 'rgba(255,255,255,0.2)' }} />
          <button
            type="button"
            onClick={handleCreatePalette}
            style={{
              display: 'flex', alignItems: 'center', gap: '5px',
              padding: '5px 12px', borderRadius: 'var(--radius, 6px)',
              background: 'var(--accent, oklch(0.55 0.15 260))',
              color: '#fff', border: 'none', cursor: 'pointer',
              fontSize: '12px', fontWeight: 600,
            }}
          >
            <Palette size={13} />
            Create palette
          </button>
          {selectedHexes.size === 2 && (
            <button
              type="button"
              onClick={handleCompare}
              style={{
                display: 'flex', alignItems: 'center', gap: '5px',
                padding: '5px 12px', borderRadius: 'var(--radius, 6px)',
                background: 'rgba(255,255,255,0.15)',
                color: '#fff', border: 'none', cursor: 'pointer',
                fontSize: '12px', fontWeight: 500,
              }}
            >
              <GitCompare size={13} />
              Compare
            </button>
          )}
        </div>
      )}
    </div>
  );
}

interface ColorHistoryItemProps {
  item: ColorHistoryRecord;
  isActive: boolean;
  isSelecting: boolean;
  isSelected: boolean;
  onUse: () => void;
  onToggleSelect: () => void;
}

function ColorHistoryItem({ item, isActive, isSelecting, isSelected, onUse, onToggleSelect }: ColorHistoryItemProps) {
  const { copyColor, saveToFavorites, copiedField } = useColorActions();
  const sourceIcon = {
    manual: '✏️',
    eyedropper: '💧',
    import: '📥',
    palette: '🎨',
  }[item.source || 'manual'];

  const timeAgo = formatTimeAgo(item.timestamp);
  const isCopied = copiedField === `history-${item.hex}`;

  return (
    <div
      className={`hist-swatch group ${isActive ? 'hist-swatch--active' : ''} ${isSelected ? 'hist-swatch--selected' : ''}`}
      style={{
        backgroundColor: item.hex,
        outline: isSelected ? '2px solid var(--accent, oklch(0.55 0.15 260))' : undefined,
        outlineOffset: isSelected ? '1px' : undefined,
        cursor: isSelecting ? 'pointer' : undefined,
        overflow: 'hidden',
      }}
      title={`${item.hex}\n${item.displayValue}\n${sourceIcon} ${item.source || 'manual'}\n${timeAgo}`}
      onClick={isSelecting ? onToggleSelect : undefined}
    >
      {/* Selection checkbox overlay — shown on top of everything in select mode */}
      {isSelecting && (
        <div style={{
          position: 'absolute', top: '2px', left: '2px', zIndex: 3,
          background: 'rgba(255,255,255,0.85)', borderRadius: '3px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '18px', height: '18px',
        }}>
          {isSelected
            ? <CheckSquare size={14} style={{ color: 'var(--accent, oklch(0.55 0.15 260))' }} />
            : <Square size={14} style={{ color: 'var(--ink-3, #999)' }} />
          }
        </div>
      )}

      {/* Hex overlay — always rendered for layout, but pointer-events disabled in select mode */}
      <button
        type="button"
        className="hist-swatch__hex"
        onClick={isSelecting ? undefined : () => copyColor(item.hex, `history-${item.hex}`)}
        title={isSelecting ? item.hex : (isCopied ? 'Copied ✓' : 'Copy hex')}
        aria-label={isCopied ? 'Copied' : `Copy ${item.hex} to clipboard`}
        style={isSelecting ? { pointerEvents: 'none', opacity: 0 } : undefined}
        tabIndex={isSelecting ? -1 : 0}
      >
        {isCopied ? '✓' : item.hex.slice(0, 7)}
      </button>
      {isActive && <div className="hist-swatch__active-dot" />}

      {/* Action buttons — only in normal mode */}
      {!isSelecting && (
        <div className="hist-swatch__actions">
          <button
            type="button"
            className="hist-swatch__action-btn"
            onClick={() => onUse()}
            title="Use as active color"
            aria-label={`Use ${item.hex} as active color`}
          >
            <ArrowUpRight size={12} />
          </button>
          <button
            type="button"
            className="hist-swatch__action-btn"
            onClick={() => saveToFavorites(item.hex)}
            title="Save to favorites"
            aria-label={`Save ${item.hex} to favorites`}
          >
            <Star size={12} />
          </button>
        </div>
      )}
    </div>
  );
}

function formatTimeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  
  return new Date(timestamp).toLocaleDateString();
}
